import _ from "lodash";

/** Reserved slot — must match engine IDLE_ANIMATION_SLOT. */
export const IDLE_ANIMATION_SLOT = "idle";

/**
 * Build playAnimation args for idle replay (preset key or inline steps).
 * @param {object} actionArgs
 * @returns {object | null}
 */
export function buildIdleAnimationReplayArgs(actionArgs) {
  if (!actionArgs || actionArgs.slot !== IDLE_ANIMATION_SLOT) return null;

  const slot = IDLE_ANIMATION_SLOT;
  const chainOpts = {};
  if (actionArgs.repeat !== undefined) chainOpts.repeat = actionArgs.repeat;
  if (actionArgs.yoyo !== undefined) chainOpts.yoyo = actionArgs.yoyo;
  if (actionArgs.delay !== undefined) chainOpts.delay = actionArgs.delay;
  if (actionArgs.repeatDelay !== undefined) chainOpts.repeatDelay = actionArgs.repeatDelay;

  if (typeof actionArgs.key === "string") {
    return { key: actionArgs.key, slot, ...chainOpts };
  }

  if (Array.isArray(actionArgs.steps) && actionArgs.steps.length > 0) {
    return { steps: actionArgs.steps, slot, ...chainOpts };
  }

  return null;
}

/**
 * @param {object} actionArgs
 * @returns {string}
 */
export function idleAnimationLabel(actionArgs) {
  if (typeof actionArgs?.key === "string") return actionArgs.key;
  if (Array.isArray(actionArgs?.steps) && actionArgs.steps.length) return "inline";
  return "idle";
}

/**
 * First playAnimation action on the reserved idle slot for a component.
 * @param {object[]} eventBindings
 * @param {string} componentId
 * @returns {{ preset: string | null, replayArgs: object | null, label: string } | null}
 */
export function findIdleAnimationAction(eventBindings, componentId) {
  for (const binding of eventBindings || []) {
    for (const action of binding.actions || []) {
      if (
        action.actionTarget === componentId &&
        action.actionName === "playAnimation" &&
        action.actionArgs?.slot === IDLE_ANIMATION_SLOT
      ) {
        const replayArgs = buildIdleAnimationReplayArgs(action.actionArgs);
        if (!replayArgs) return null;
        return {
          preset: action.actionArgs?.key || null,
          replayArgs,
          label: idleAnimationLabel(action.actionArgs),
        };
      }
    }
  }
  return null;
}

/**
 * Map componentId → idle replay info for components with idle-slot playAnimation bindings.
 * @param {object[]} eventBindings
 * @returns {Record<string, { preset: string | null, replayArgs: object, label: string }>}
 */
export function buildIdleAnimationComponentMap(eventBindings) {
  const map = {};
  for (const binding of eventBindings || []) {
    for (const action of binding.actions || []) {
      if (
        action.actionName !== "playAnimation" ||
        action.actionArgs?.slot !== IDLE_ANIMATION_SLOT ||
        !action.actionTarget
      ) {
        continue;
      }
      const componentId = action.actionTarget;
      if (map[componentId]) continue;
      const replayArgs = buildIdleAnimationReplayArgs(action.actionArgs);
      if (!replayArgs) continue;
      map[componentId] = {
        preset: action.actionArgs?.key || null,
        replayArgs,
        label: idleAnimationLabel(action.actionArgs),
      };
    }
  }
  return map;
}

/**
 * @param {object} playEngineConfig
 * @param {string} componentId
 * @returns {boolean}
 */
export function getIdleAnimationEnabled(playEngineConfig, componentId) {
  const components = _.get(playEngineConfig, "components") || [];
  const comp = components.find((c) => c.id === componentId);
  const enabled = _.get(comp, "config.idleAnimation.enabled");
  return enabled ?? true;
}

/**
 * Whether Version Settings should show the idle animation toggle.
 * @param {object[]} eventBindings
 * @param {object} playEngineConfig
 * @param {string} componentId
 */
export function shouldShowIdleAnimationToggle(eventBindings, playEngineConfig, componentId) {
  if (findIdleAnimationAction(eventBindings, componentId)) return true;
  const components = _.get(playEngineConfig, "components") || [];
  const comp = components.find((c) => c.id === componentId);
  return _.get(comp, "config.idleAnimation") != null;
}
